import { create } from 'zustand';
import { adminSessionApi, AdminSessionDto } from './adminSessionApi';

interface LogoutDialogState {
  visible: boolean;
  sessionId: string | null;
  userName: string;
}

interface AdminSessionStoreState {
  loading: boolean;
  error: string | null;
  data: AdminSessionDto[] | null;
  totalCount: number;

  logoutDialog: LogoutDialogState;
  isLoggingOut: boolean;

  loadSessions: () => Promise<void>;
  handleLogoutClick: (session: AdminSessionDto) => void;
  handleLogoutConfirm: () => Promise<void>;
  handleLogoutCancel: () => void;
  handleLogoutAllUserSessions: (userId: number, userName: string) => Promise<void>;
}

export const useAdminSessionStore = create<AdminSessionStoreState>((set, get) => ({
  loading: false,
  error: null,
  data: null,
  totalCount: 0,

  logoutDialog: {
    visible: false,
    sessionId: null,
    userName: '',
  },
  isLoggingOut: false,

  loadSessions: async () => {
    try {
      set({ loading: true, error: null });

      const response = await adminSessionApi.getAdminSessionList();

      set({
        loading: false,
        data: response.data.sessions,
        totalCount: response.data.totalCount,
      });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Chyba při načítání sessions' });
    }
  },

  handleLogoutClick: (session: AdminSessionDto) => {
    const userName = `${session.firstName || ''} ${session.familyName || ''}`.trim() || session.email || 'Uživatel';
    set({
      logoutDialog: {
        visible: true,
        sessionId: session.sessionId,
        userName,
      },
    });
  },

  handleLogoutConfirm: async () => {
    const { logoutDialog } = get();
    if (!logoutDialog.sessionId) return;

    set({ isLoggingOut: true });
    try {
      await adminSessionApi.deleteAdminSession(logoutDialog.sessionId);
      set({
        logoutDialog: { visible: false, sessionId: null, userName: '' },
        isLoggingOut: false,
      });
      await get().loadSessions();
    } catch (error) {
      console.error('Chyba při odhlašování session:', error);
      set({ isLoggingOut: false });
    }
  },

  handleLogoutCancel: () => {
    set({
      logoutDialog: { visible: false, sessionId: null, userName: '' },
    });
  },

  handleLogoutAllUserSessions: async (userId: number, userName: string) => {
    set({ isLoggingOut: true });
    try {
      await adminSessionApi.deleteAdminUserSessions(userId);
      set({ isLoggingOut: false });
      await get().loadSessions();
    } catch (error) {
      console.error(`Chyba při odhlašování všech sessions uživatele ${userName}:`, error);
      set({ isLoggingOut: false });
    }
  },
}));
